import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { isArray, some, sortBy } from 'lodash';
import { SiteSummaryContext } from '../../../shared/contexts/site-summary.context';
import { ZiroModalService } from '../../../shared/services/ziro-modal.service';
import { ServersContext } from '../../contexts/servers.context';
import {
  BottomNavService,
  BottomNavUpdateButtonsList,
  BottomNavUpdateButtonState,
} from '../../../shared/bottom-nav/bottom-nav.service';
import { ToastTypes } from '../../../shared/services/abstract/toast.service.abstract';
import { TranslateService } from '@ngx-translate/core';
import { BottomNavButtonState } from '../../../shared/bottom-nav/bottom-nav.component';
import { SystemStatusContext } from '../../../shared/contexts/system-status.context';
import {
  ClusterResult,
  HealthStatus,
  Server,
  State,
  Status,
  StatusCategory,
  SystemHealthStatus,
} from '../../../shared/models/generated/ziroModels';
import { combineLatest, Observable, ReplaySubject, Subscription } from 'rxjs';
import { ToastService } from '../../../shared/services/toast.service';
import { ButtonStyles, ButtonTypes } from '../../../button/button.component';
import { ZiroIcons } from '../../../shared/models/ziro-icons.enum';
import { UcMetadataCacheContext } from '../../../shared/contexts/uc-metadata-cache.context';
import { BreadcrumbsService } from '../../../shared/breadcrumbs/breadcrumbs.service';
import { AdminAddEditServerModalComponent } from './add-edit-server-modal.component';
import { ZiroModalDetailedModalOptions } from '../../../modals/detailed-modal/detailed-modal.component';
import { DatatableColumn, DatatableRow } from '../../../shared/tables/datatable/datatable.models';
import { delayWhen, map, switchMap, tap } from 'rxjs/operators';
import { SearchFieldConfigContext } from '../../../shared/contexts/search-field-config.context';

interface ServersRow extends DatatableRow {
  id: string;
  description: string;
  hostAddress: string;
  serverType: string;
  status: string;
}

@Component({
  selector: 'ziro-admin-servers',
  templateUrl: './servers.component.html',
  styleUrls: ['../../admin-page.scss'],
  standalone: false,
})
export class AdminServersComponent implements OnInit, OnDestroy {
  private _siteSummaryContext = inject(SiteSummaryContext);
  private _ziroModalService = inject(ZiroModalService);
  private _toastService = inject(ToastService);
  private _serversContext = inject(ServersContext);
  private _bottomNavService = inject(BottomNavService);
  private _translateService = inject(TranslateService);
  private _systemStatusContext = inject(SystemStatusContext);
  private _ucMetadataCacheContext = inject(UcMetadataCacheContext);
  private _breadcrumbsService = inject(BreadcrumbsService);
  private _searchFieldConfigContext = inject(SearchFieldConfigContext);
  private readonly _reloadButtonId: string = 'reload-config-btn';
  private readonly _addServerButtonId: string = 'admin-add-server';

  isLoading = true;
  serverStatuses: Map<string, Status>;
  licenseStatus: HealthStatus;
  serverStatusesNew: HealthStatus[] = [];
  bottomNavButtons = [
    {
      id: this._reloadButtonId,
      dataAutomation: 'reload-config-btn',
      label: 'tkey;menu.admin.reload',
      buttonClass: ButtonStyles.PRIMARY,
      icon: ZiroIcons.REFRESH,
      state: {
        pending: false,
        buttonDisableState: { disabled: false },
      },
      cb: () => {
        this._reloadConfig();
      },
    },
    {
      id: this._addServerButtonId,
      dataAutomation: 'admin-add-server',
      label: 'tkey;admin.servers.add.server.button',
      icon: ZiroIcons.ADD,
      tooltipKey: 'tkey;admin.servers.add.server.button.tooltip',
      buttonClass: ButtonStyles.PRIMARY,
      state: {
        pending: false,
        buttonDisableState: { disabled: true, tooltipKey: 'tkey;admin.servers.add.server.button.tooltip' },
        tooltipVisible: true,
      },
      cb: () => {
        this._openEditModal();
      },
    },
  ];
  statusOptions = [...(Object.values(State) as string[])];
  typeOptions = ['CUCM', 'IM & Presence', 'Unity', 'UCCX', 'PCCE'];
  tableDataAutomation = 'servers-table';
  columns: DatatableColumn<ServersRow>[] = [
    {
      name: 'id',
      columnSize: 'col-sm-1',
      label: 'tkey;admin.servers.id.label',
    },
    {
      name: 'description',
      columnSize: 'col-sm-3',
      label: 'tkey;admin.servers.description.label',
    },
    {
      name: 'hostAddress',
      columnSize: 'col-sm-3',
      label: 'tkey;admin.servers.host.address.label',
    },
    {
      name: 'serverType',
      columnSize: 'col-sm-2',
      label: 'tkey;admin.servers.server.type.label',
    },
    {
      name: 'status',
      columnSize: 'col-sm-2',
      label: 'tkey;system_status.server.status',
    },
  ];
  data: ServersRow[] = [];

  private _servers: Server[] = [];
  private _reloadingConfigurations = false;
  private _clusters: ClusterResult[] = [];
  private _subscription = new Subscription();
  private _isFirstRun = true;
  private _isRefreshDone = new ReplaySubject<void>(1);

  private static _getServerUrl(server: Server): string {
    const baseUrl = 'https://' + server.hostAddress;
    return server.serverType === 'PCCE' ? baseUrl + '/cceadmin' : baseUrl;
  }

  ngOnInit(): void {
    this._breadcrumbsService.updateBreadcrumbs([{ label: 'tkey;admin.servers.config.title' }]);
    this._bottomNavService.dispatch(new BottomNavUpdateButtonsList(this.bottomNavButtons));
    this.serverStatuses = new Map();

    const serverSiteSub = combineLatest([this._serversContext.state$, this._siteSummaryContext.state$]).subscribe(
      ([servers, siteSummary]) => {
        this._servers = sortBy(servers, ['serverType', 'description']);
        this._clusters = siteSummary.clusters;
        if (this._isFirstRun) {
          this._isFirstRun = false;
          this._isRefreshDone.next();
        }
      },
    );
    this._subscription.add(serverSiteSub);

    const systemStatusContextSub = this._systemStatusContext.state$.subscribe((action: SystemHealthStatus) => {
      this._loadSystemAlertState(action);
      if (!this.isLoading) {
        this._generateTable();
      }
    });
    this._subscription.add(systemStatusContextSub);

    const refreshSub = this._isRefreshDone.subscribe(() => {
      this._generateTable();
      this.isLoading = false;
    });
    this._subscription.add(refreshSub);
  }

  ngOnDestroy(): void {
    this._breadcrumbsService.clearBreadcrumbs();
    this._subscription.unsubscribe();
  }

  getBadServers(): Server[] {
    if (!this._hasServers() || this.serverStatuses.size === 0) {
      return [];
    }

    return this._servers.filter((server: Server) => {
      return this.serverStatuses.has(server.hostAddress)
        ? this.serverStatuses.get(server.hostAddress).state === State.ERROR
        : false;
    });
  }

  isTypeMatch(filters: string[], row: ServersRow): boolean {
    return !filters.length || filters.includes(row.serverType);
  }

  isStatusMatch(filters: string[], row: ServersRow): boolean {
    const status = this.serverStatuses.has(row.hostAddress) ? this.serverStatuses.get(row.hostAddress).state : 'Ok';

    return (
      !filters.length || filters.map((filter: string) => filter.toLowerCase()).includes(status.toLocaleLowerCase())
    );
  }

  private _openEditModal(server?: Server, isEdit?: boolean): void {
    if (!server) {
      server = {
        port: 8443,
        disabled: false,
      } as Server;
    }
    const options = {
      modalViewProperties: {
        title: isEdit ? 'tkey;admin.servers.modal.edit.title' : 'tkey;admin.servers.modal.add.title',
        server,
        servers: this._servers,
        submitFn: (entity: Server): Observable<void> => {
          return this._saveServer(entity).pipe(
            delayWhen(() => this._refreshContexts()),
            map(() => {}),
          );
        },
      },
      bodyClass: AdminAddEditServerModalComponent,
      size: 'lg',
    } as ZiroModalDetailedModalOptions;

    this._ziroModalService
      .openDetailedModal(() => options.modalViewProperties, options)
      .subscribe((updatedServer) => {
        if (updatedServer) {
          this._toastService.push(
            ToastTypes.SUCCESS,
            ZiroIcons.SAVE_SUCCESS,
            server?.id ? 'tkey;admin.servers.update.success.text' : 'tkey;admin.servers.add.success.text',
            'tkey;admin.servers.add.success.text',
          );
        }
      });
  }

  private _openDeleteModal(server: Server): void {
    if (this._isServerUsedByCluster(server)) {
      return;
    }
    const options = {
      windowClass: 'delete-button-modal',
      modalViewProperties: {
        promptBody: this._translateService.instant('tkey;admin.servers.delete.confirm.text', {
          description: server.description,
        }),
        icon: ZiroIcons.DELETE_SMALL,
        iconClass: 'text-danger',
        displayCloseButton: true,
        buttons: [
          {
            label: 'tkey;dialogs.button.cancel',
            buttonClass: ButtonStyles.OUTLINE_PRIMARY,
            dataAutomation: 'confirmation-modal-cancel-button',
          },
          {
            label: 'tkey;dialogs.button.delete',
            buttonClass: ButtonStyles.OUTLINE_DANGER,
            dataAutomation: 'confirmation-modal-confirm-button',
            cb: () => this._onConfirmDelete(server),
          },
        ],
      },
    };
    this._ziroModalService.openPromptModal(() => options.modalViewProperties, options);
  }

  private _saveServer(entity: Server): Observable<Server> {
    if (entity.id) {
      return this._serversContext.put(entity);
    } else {
      return this._serversContext.post(entity);
    }
  }

  private _isErrorPresentOnServer(server: Server): boolean {
    return this.serverStatuses.has(server.hostAddress)
      ? this.serverStatuses.get(server.hostAddress).state === State.ERROR
      : false;
  }

  private _isWarningPresentOnSever(server: Server): boolean {
    return (
      this.serverStatuses.has(server.hostAddress) && this.serverStatuses.get(server.hostAddress).state === State.WARNING
    );
  }

  private _isServerUsedByCluster(server: Server): boolean {
    if (server.serverType === 'CUCM') {
      return some(this._clusters, (cluster) => cluster.cucmServerId === server.id);
    } else if (server.serverType === 'IM & Presence') {
      return some(this._clusters, (cluster) => cluster.impServerId === server.id);
    } else if (server.serverType === 'PCCE') {
      return some(this._clusters, (cluster) => cluster.pcceServerId === server.id);
    } else if (server.serverType === 'Unity') {
      return some(this._clusters, (cluster) => {
        return some(cluster.sites, (site) => site.unityServerId === server.id);
      });
    }
    return false;
  }

  private _onConfirmDelete(server: Server): Observable<any> {
    return this._serversContext.delete(server.id).pipe(
      switchMap(() => this._refreshContexts()),
      tap(() => {
        this._toastService.pushDeleteToast('tkey;admin.servers.config.name', server.description);
      }),
    );
  }

  private _refreshContexts = (): Observable<void> => {
    this.isLoading = true;

    return combineLatest([
      this._siteSummaryContext.refreshSiteSummary(),
      this._ucMetadataCacheContext.reloadUcMetadataCache(),
      this._serversContext.getAll(),
      this._searchFieldConfigContext.refresh(),
    ]).pipe(
      switchMap(() => this._systemStatusContext.refreshSystemStatus()),
      map(() => {
        this._isRefreshDone.next();
      }),
    );
  };

  private _loadSystemAlertState(systemAlertState: SystemHealthStatus): void {
    if (systemAlertState) {
      this.serverStatusesNew = [];
      systemAlertState.healthStatuses.forEach((status) => {
        if (status.category === StatusCategory.LICENSES) {
          this.licenseStatus = status;
        }

        if (status.category === StatusCategory.ENDPOINTS) {
          this.serverStatusesNew.push(status);
          this.serverStatuses.clear();
        }
      });
      this.serverStatusesNew.forEach((newStatus) => {
        const serviceStatus = {
          cause: newStatus.cause,
          state: newStatus.state,
        };
        const url = newStatus.description.substring(
          newStatus.description.lastIndexOf('[') + 1,
          newStatus.description.lastIndexOf(']'),
        );
        this.serverStatuses.set(url, serviceStatus);
      });
      const isNoLicense =
        this.licenseStatus.state === State.WARNING && this.licenseStatus.cause === 'No license installed';

      this._bottomNavService.dispatch(
        new BottomNavUpdateButtonState({
          id: this._addServerButtonId,
          state: {
            buttonDisableState: { disabled: isNoLicense, tooltipKey: 'tkey;admin.servers.add.server.button.tooltip' },
            tooltipVisible: isNoLicense,
          },
        }),
      );
    }
  }

  private _getServerStatus(server: Server): { status: string; html: string } {
    if (this._isWarningPresentOnSever(server)) {
      const status = this.serverStatuses.get(server.hostAddress)?.cause;
      return {
        status: status,
        html: `
          <span class="badge me-2 bg-warning" data-automation="misconfigured-server-error">
            ${status}
          </span>
        `,
      };
    } else if (this._isErrorPresentOnServer(server)) {
      const status = this.serverStatuses.get(server.hostAddress)?.cause;
      return {
        status: status,
        html: `
          <span class="badge me-2 bg-danger" data-automation="misconfigured-server-error">
            ${status}
          </span>
        `,
      };
    } else {
      const status = this.serverStatuses.get(server.hostAddress)?.state;
      return {
        status: status,
        html: `
          <span class="badge me-2 bg-success" data-automation="misconfigured-server-error">
            ${status ? status : ''}
          </span>
        `,
      };
    }
  }

  private _generateTable(): void {
    this.data = this._servers.map((server: Server): ServersRow => {
      const status = this._getServerStatus(server);
      return {
        cssClass: this._isWarningPresentOnSever(server)
          ? 'table-warning'
          : this._isErrorPresentOnServer(server)
            ? 'table-danger'
            : null,
        id: server.id.toString(),
        description: server.description,
        hostAddress: server.hostAddress,
        serverType: server.serverType,
        status: status.status ?? '',
        html: {
          hostAddress: `<a href="${AdminServersComponent._getServerUrl(server)}" target="_blank">${
            server.hostAddress
          }</a>`,
          status: status.html,
        },
        actions: [
          {
            dataAutomation: 'server-edit',
            style: ButtonStyles.OUTLINE_PRIMARY,
            type: ButtonTypes.BUTTON,
            icon: ZiroIcons.EDIT,
            onClick: () => this._openEditModal(server, true),
          },
          {
            dataAutomation: 'server-delete',
            style: ButtonStyles.OUTLINE_DANGER,
            type: ButtonTypes.BUTTON,
            icon: ZiroIcons.DELETE_SMALL,
            onClick: () => this._openDeleteModal(server),
            disabled: server.disabled || this._isServerUsedByCluster(server),
            disabledTooltip: server.disabled
              ? 'tkey;admin.servers.delete.disabled.tooltip.disabled_server'
              : 'tkey;admin.servers.delete.disabled.tooltip.in_use',
            isTooltipDisabled: !(server.disabled || this._isServerUsedByCluster(server)),
          },
        ],
      };
    });

    console.warn(`Initial server state had ${this.getBadServers().length} invalid server configurations`);
  }

  private _reloadConfig(): void {
    if (!this._reloadingConfigurations) {
      this.isLoading = true;
      this._reloadingConfigurations = true;
      this._updateButtonState(this._reloadButtonId, {
        pending: true,
        buttonDisableState: { disabled: true },
      });
      this._updateButtonState(this._addServerButtonId, {
        pending: false,
        buttonDisableState: { disabled: true, tooltipKey: 'tkey;admin.servers.add.server.button.tooltip' },
      });

      this._refreshContexts().subscribe(() => {
        this._reloadingConfigurations = false;
        this._updateButtonState(this._reloadButtonId, {
          pending: false,
          buttonDisableState: { disabled: false, tooltipKey: '' },
        });
        this._updateButtonState(this._addServerButtonId, {
          pending: false,
          buttonDisableState: {
            disabled: this.licenseStatus.state === State.WARNING && this.licenseStatus.cause === 'No license installed',
            tooltipKey: '',
          },
        });

        this._isRefreshDone.next();
        this._toastService.push(
          ToastTypes.SUCCESS,
          ZiroIcons.REFRESH,
          'tkey;pages.admin.configurationreloaded.toast.title',
          'tkey;pages.admin.configurationreloaded.toast.message',
        );
      });
    }
  }

  private _updateButtonState(id: string, state: BottomNavButtonState): void {
    this._bottomNavService.dispatch(
      new BottomNavUpdateButtonState({
        id: id,
        state: state,
      }),
    );
  }

  private _hasServers = (): boolean => {
    return isArray(this._servers) && !!this._servers.length;
  };
  protected readonly String = String;
}
